import { AlertColor, AlertTitle, Box, Paper, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import AlertSlider from '../../common/components/AlertSlider';
import AppSpinner from '../../common/components/AppSpinner';
import GeneralErrorPage from '../../common/components/GeneralErrorPage';
import IconAlert from '../../common/components/IconAlert';
import { useScan } from '../../new-scan/actions/scan-actions';
import {
  handleConnectorsPageQuery,
  handleSearchConnector,
  loadConnectorsByFamily,
  resetConnectorsStore,
} from '../actions/connector-actions';
import {
  dismissValidationMsg,
  validateAllCredentials,
} from '../actions/credentials-actions';
import ConnectorCard from '../components/ConnectorCard';
import ConnectorsListToolbar from '../components/ConnectorsListToolbar';
import ConnectorsStore from '../stores/connectors-store';
import { generateValidationMsg } from '../utils/credentials-utils';

const ConnectorsContainer: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const {
    connectors,
    connectorsByFamily,
    loadingConnectors,
    filteredConnectorsByFamily,
    credentialsValidationInProgress,
    showValidationMsg,
    validationMsgObj,
  } = useSnapshot(ConnectorsStore);
  const { searchValue } = useSnapshot(ConnectorsStore, { sync: true });
  const { isScanning, isScanAllDisabled } = useScan();

  const [searchParams] = useSearchParams();
  const isIdp = searchParams.get('idp');
  const idpError = searchParams.get('idpError');
  const connectorId = searchParams.get('connectorId');

  useEffect(() => {
    if (connectorsByFamily.length === 0) {
      loadConnectorsByFamily(true);
    } else {
      handleConnectorsPageQuery(connectorId, isIdp, idpError);
      const searchQuery = searchParams.get('search');
      searchQuery && handleSearchConnector(searchQuery);
    }

    return () => {
      resetConnectorsStore();
    };
  }, [connectorId, connectorsByFamily.length, idpError, isIdp, searchParams]);

  useEffect(() => {
    if (showValidationMsg) {
      ConnectorsStore.validationMsgObj = generateValidationMsg(connectors);
    }
  }, [connectors, showValidationMsg]);

  if (loadingConnectors) {
    return <AppSpinner text='Loading Connectors' />;
  }

  return (
    <>
      <Box width='100%'>
        {showValidationMsg && (
          <AlertSlider onClose={dismissValidationMsg}>
            <IconAlert color={validationMsgObj?.color as AlertColor}>
              <AlertTitle sx={{ margin: '0 !important' }}>
                {validationMsgObj?.color === 'error'
                  ? 'Connectivity issues found'
                  : 'Connectivity Verified!'}
              </AlertTitle>
              {validationMsgObj?.msg}
            </IconAlert>
          </AlertSlider>
        )}

        <Box
          display='flex'
          component={Paper}
          flexDirection='column'
          p={1}
          elevation={2}>
          <ConnectorsListToolbar
            isScanAllDisabled={isScanAllDisabled}
            isScanning={isScanning}
            handleSearch={handleSearchConnector}
            credentialsValidationInProgress={credentialsValidationInProgress}
            validateAllCredentials={validateAllCredentials}
            searchValue={searchValue}
          />
        </Box>

        {!connectorsByFamily.length && (
          <GeneralErrorPage
            errorTitle="Oops! We've got a problem"
            errorMsg='Failed to load connectors, please try again'
          />
        )}
        {!filteredConnectorsByFamily.length && (
          <GeneralErrorPage
            errorTitle='No results found'
            errorMsg='No connectors match your search'
          />
        )}
        {filteredConnectorsByFamily.map(familyObject => {
          return (
            <React.Fragment key={familyObject.family}>
              <Box mt={1} mb={1}>
                <Typography
                  color='text.primary'
                  variant='body1'
                  fontWeight={300}
                  fontSize='1.25rem'>
                  {familyObject.familyDisplayName}
                </Typography>
                <Box
                  display='flex'
                  flexDirection='row'
                  justifyContent='flex-start'
                  mt={1}
                  mb={4}
                  gap={2.5}
                  flexWrap='wrap'>
                  {familyObject.connectors.map(connector => {
                    const aliasConnector =
                      connector.connectorToUse || connector;
                    return (
                      <ConnectorCard
                        builtIn={aliasConnector.isOxBuiltIn}
                        comingSoon={aliasConnector.comingSoon}
                        displayName={
                          connector.aliasFor
                            ? connector.displayName
                            : aliasConnector.displayName
                        }
                        configured={
                          aliasConnector.isConfigured &&
                          !aliasConnector.comingSoon
                        }
                        iconUrlOriginalToUse={connector.iconURL}
                        id={aliasConnector.id}
                        parentId={connector.id}
                        key={connector.id}
                        discovered={!!connector.isDiscovered}
                        tokenExpired={!!aliasConnector.isConnectorInvalid}
                        noReposFound={!!connector.isEmptyOfRepos}
                      />
                    );
                  })}
                </Box>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </>
  );
};

export default ConnectorsContainer;
